import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Collapse,
  Drawer,
  IconButton,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChevronDown, ChevronUp, HelpCircle, X } from "feather-icons-react";
import { useTranslation } from "react-i18next";

import {
  GradeRange,
  JobSeniorityCombination,
  PositionAnswersMap,
} from "../../hooks";
import {
  GradeResultsDisplay,
  JobSeniorityPointsTab,
  PositionListItem,
  PositionListTab,
  TraitSettingsTab,
} from "./components";

import { Button } from "@components/base";
import { GradeEstimatorResponseDto } from "@api/hooks/paygap";

// ── Collapsible Rollup for drawer sections ──

interface DrawerRollupProps {
  title: string;
  description: string;
  whyText: string;
  isOpen: boolean;
  onToggle: () => void;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}

const DrawerRollup: FC<DrawerRollupProps> = ({
  title,
  description,
  whyText,
  isOpen,
  onToggle,
  scrollRef,
  children,
}) => (
  <Box
    ref={scrollRef}
    sx={{
      border: "1px solid",
      borderColor: "grey.200",
      borderRadius: 1,
    }}
  >
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        cursor: "pointer",
        px: 2,
        py: 1.5,
        bgcolor: isOpen ? "transparent" : "grey.50",
        "&:hover": { bgcolor: "action.hover" },
      }}
      onClick={onToggle}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        <Typography variant="subtitle2">{title}</Typography>
      </Box>
      <Tooltip title={whyText}>
        <IconButton
          size="small"
          onClick={(e): void => e.stopPropagation()}
        >
          <HelpCircle size={14} />
        </IconButton>
      </Tooltip>
    </Box>
    <Collapse in={isOpen}>
      <Box px={2} pb={2}>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          {description}
        </Typography>
        {children}
      </Box>
    </Collapse>
  </Box>
);

// ── Rollup keys in display order ──
type RollupKey = "traitTable" | "positionList" | "gradeModeling" | "setPoints";

// Maps stepper step index -> which rollup to open
const STEP_TO_ROLLUP: RollupKey[] = [
  "positionList",  // Step 1: Upload existing grading
  "traitTable",    // Step 2: Set job criteria
  "gradeModeling", // Step 3: Run grade modeling
  "setPoints",     // Step 4: Estimate grades
  "setPoints",     // Step 5: Update dataset
];

type HighlightedButtonType = "runGradeModeling" | "overwriteGrades" | null;

// ── Main Drawer ──

interface GradeEstimatorDrawerProps {
  open: boolean;
  onClose: () => void;
  traitMaxValues: Record<string, number>;
  setTraitMaxValues: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  comboPoints: Record<string, Record<string, number>>;
  setComboPoints: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, number>>>
  >;
  questionnaireAnswers: Record<string, PositionAnswersMap>;
  setQuestionnaireAnswers: React.Dispatch<
    React.SetStateAction<Record<string, PositionAnswersMap>>
  >;
  parentTotals: Record<string, number>;
  totalMaxPoints: number;
  uniqueJobSeniorities: JobSeniorityCombination[];
  isLoadingPersonnel: boolean;
  displayGrades: GradeEstimatorResponseDto[] | null;
  gradeRanges: GradeRange[];
  isModelLoading: boolean;
  modelError: string | null;
  showSaveSuccess: boolean;
  canRunEstimation: boolean;
  onRunGradeEstimation: () => void;
  onSaveGrades: () => void;
  onSaveSettings: () => void;
  onSaveQuestionnaireAnswers: (
    comboKey: string,
    newAnswers: PositionAnswersMap,
    updatedComboPoints: Record<string, Record<string, number>>,
  ) => void;
  onApplyGrades: () => void;
  getGradeForPoints: (sum: number) => string;
  positionList: PositionListItem[];
  setPositionList: React.Dispatch<React.SetStateAction<PositionListItem[]>>;
  onSavePositionList: (list: PositionListItem[]) => void;
  onSaveJobTitleMatches: (
    updatedJobTitleMatches: Record<string, string | null>,
    updatedMatchedGrades: Record<string, number | null>,
  ) => void;
  // Job title match props
  jobTitleMatches: Record<string, string | null>;
  setJobTitleMatches: React.Dispatch<
    React.SetStateAction<Record<string, string | null>>
  >;
  matchedGrades: Record<string, number | null>;
  setMatchedGrades: React.Dispatch<
    React.SetStateAction<Record<string, number | null>>
  >;
  onSyncPositions: () => void;
}

export const GradeEstimatorDrawer: FC<GradeEstimatorDrawerProps> = ({
  open,
  onClose,
  traitMaxValues,
  setTraitMaxValues,
  comboPoints,
  setComboPoints,
  questionnaireAnswers,
  setQuestionnaireAnswers,
  parentTotals,
  totalMaxPoints,
  uniqueJobSeniorities,
  isLoadingPersonnel,
  displayGrades,
  gradeRanges,
  isModelLoading,
  modelError,
  showSaveSuccess,
  canRunEstimation,
  onRunGradeEstimation,
  onSaveGrades,
  onSaveSettings,
  onSaveQuestionnaireAnswers,
  onApplyGrades,
  getGradeForPoints,
  positionList,
  setPositionList,
  onSavePositionList,
  onSaveJobTitleMatches,
  jobTitleMatches,
  setJobTitleMatches,
  matchedGrades,
  setMatchedGrades,
  onSyncPositions,
}) => {
  const { t } = useTranslation();
  const [activeGuideStep, setActiveGuideStep] = useState<number>(0);
  const [highlightedButton, setHighlightedButton] =
    useState<HighlightedButtonType>(null);

  // Rollup open state
  const [openRollups, setOpenRollups] = useState<Record<RollupKey, boolean>>({
    traitTable: true,
    positionList: false,
    gradeModeling: false,
    setPoints: false,
  });

  // Refs for scrolling
  const traitTableRef = useRef<HTMLDivElement>(null);
  const positionListRef = useRef<HTMLDivElement>(null);
  const gradeModelingRef = useRef<HTMLDivElement>(null);
  const setPointsRef = useRef<HTMLDivElement>(null);

  const rollupRefs: Record<RollupKey, React.RefObject<HTMLDivElement | null>> = {
    traitTable: traitTableRef,
    positionList: positionListRef,
    gradeModeling: gradeModelingRef,
    setPoints: setPointsRef,
  };

  useEffect(() => {
    if (open) {
      setActiveGuideStep(0);
      setHighlightedButton(null);
      setOpenRollups({
        traitTable: true,
        positionList: false,
        gradeModeling: false,
        setPoints: false,
      });
    }
  }, [open]);

  const toggleRollup = useCallback((key: RollupKey): void => {
    setOpenRollups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const guideSteps = [
    t("paygap.gradeEstimator.guideStep1", "1. Upload existing grading"),
    t("paygap.gradeEstimator.guideStep2", "2. Set job criteria"),
    t("paygap.gradeEstimator.guideStep3", "3. Run grade modeling"),
    t("paygap.gradeEstimator.guideStep4", "4. Estimate grades"),
    t("paygap.gradeEstimator.guideStep5", "5. Update dataset"),
  ];

  const STEP_HIGHLIGHT: (HighlightedButtonType)[] = [
    null,
    null,
    "runGradeModeling",
    null,
    "overwriteGrades",
  ];

  const handleGuideStepClick = (stepIndex: number): void => {
    setActiveGuideStep(stepIndex);

    const targetRollup = STEP_TO_ROLLUP[stepIndex];

    if (targetRollup) {
      // Open the target rollup
      setOpenRollups((prev) => ({ ...prev, [targetRollup]: true }));
      setHighlightedButton(STEP_HIGHLIGHT[stepIndex]);

      // Scroll to it
      setTimeout(() => {
        rollupRefs[targetRollup]?.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    }
  };

  const handleSaveAll = useCallback((): void => {
    onSaveSettings();
    onSaveGrades();
    onSavePositionList(positionList);
  }, [onSaveSettings, onSaveGrades, onSavePositionList, positionList]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        width={{ xs: 520, sm: 1200 }}
        p={3}
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{ overflowY: "auto" }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">
            {t("paygap.gradeEstimator.title")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <X size={16} />
          </IconButton>
        </Box>

        {/* Process Guide Stepper */}
        <Box
          sx={{
            bgcolor: "grey.50",
            borderRadius: 1,
            p: 2,
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Stepper activeStep={activeGuideStep} alternativeLabel>
            {guideSteps.map((label, index) => (
              <Step
                key={index}
                completed={index < activeGuideStep}
                sx={{ cursor: "pointer" }}
                onClick={(): void => handleGuideStepClick(index)}
              >
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontSize: "0.7rem",
                      mt: 0.5,
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* ── Rollup 1: Trait Table ── */}
        <DrawerRollup
          title={t("paygap.gradeEstimator.traitTable", "Trait Table")}
          isOpen={openRollups.traitTable}
          onToggle={(): void => toggleRollup("traitTable")}
          scrollRef={traitTableRef}
          description={t(
            "paygap.gradeEstimator.traitTableDesc",
            "Define the maximum points for each evaluation criterion. These criteria measure job complexity, effort, responsibility, and working conditions.",
          )}
          whyText={t(
            "paygap.gradeEstimator.traitTableWhy",
            "The trait table sets the point scale for each job evaluation criterion. These points determine how jobs are classified into grades.",
          )}
        >
          <TraitSettingsTab
            traitMaxValues={traitMaxValues}
            setTraitMaxValues={setTraitMaxValues}
            parentTotals={parentTotals}
          />
        </DrawerRollup>

        {/* ── Rollup 2: Position List ── */}
        <DrawerRollup
          title={t("paygap.gradeEstimator.positionListButton", "Position List")}
          isOpen={openRollups.positionList}
          onToggle={(): void => toggleRollup("positionList")}
          scrollRef={positionListRef}
          description={t(
            "paygap.gradeEstimator.positionListDesc",
            "Upload or define the list of job positions. Each position can have similar job titles and an assigned grade.",
          )}
          whyText={t(
            "paygap.gradeEstimator.positionListWhy",
            "The position list maps job titles to standardized positions and grades for consistent classification.",
          )}
        >
          <PositionListTab
            positionList={positionList}
            setPositionList={setPositionList}
            onSavePositionList={onSavePositionList}
            hideSaveButton
          />
        </DrawerRollup>

        {/* ── Rollup 3: Modeling Resulting Work Classes (Grade) ── */}
        <DrawerRollup
          title={t("paygap.gradeEstimator.resulting", "Modeling Resulting Work Classes (Grade)")}
          isOpen={openRollups.gradeModeling}
          onToggle={(): void => toggleRollup("gradeModeling")}
          scrollRef={gradeModelingRef}
          description={t(
            "paygap.gradeEstimator.gradingResultDesc",
            "Run the AI grade modeling to determine the optimal number of grades and their point ranges.",
          )}
          whyText={t(
            "paygap.gradeEstimator.gradingResultWhy",
            "Grade modeling uses AI to suggest the optimal split of job grades based on trait point distribution.",
          )}
        >
          <GradeResultsDisplay
            displayGrades={displayGrades}
            gradeRanges={gradeRanges}
            isModelLoading={isModelLoading}
            modelError={modelError}
            showSaveSuccess={false}
            canRunEstimation={canRunEstimation}
            onRunGradeEstimation={onRunGradeEstimation}
            onClose={onClose}
            onSaveGrades={handleSaveAll}
            highlightRunButton={highlightedButton === "runGradeModeling"}
            hideBottomButtons
          />
        </DrawerRollup>

        {/* ── Rollup 4: Set Points by Job Title ── */}
        <DrawerRollup
          title={t("paygap.gradeEstimator.positionSeniorityButton", "Set Points by Job Title")}
          isOpen={openRollups.setPoints}
          onToggle={(): void => toggleRollup("setPoints")}
          scrollRef={setPointsRef}
          description={t(
            "paygap.gradeEstimator.pointsByJobDesc",
            "Assign evaluation points to each job title and seniority combination. Use the questionnaire or enter points directly.",
          )}
          whyText={t(
            "paygap.gradeEstimator.pointsByJobWhy",
            "Each job title receives points across the four criteria. The total determines which grade the position falls into.",
          )}
        >
          <JobSeniorityPointsTab
            uniqueJobSeniorities={uniqueJobSeniorities}
            comboPoints={comboPoints}
            setComboPoints={setComboPoints}
            questionnaireAnswers={questionnaireAnswers}
            setQuestionnaireAnswers={setQuestionnaireAnswers}
            traitMaxValues={traitMaxValues}
            parentTotals={parentTotals}
            totalMaxPoints={totalMaxPoints}
            isLoadingPersonnel={isLoadingPersonnel}
            getGradeForPoints={getGradeForPoints}
            onApplyGrades={onApplyGrades}
            onSaveQuestionnaireAnswers={onSaveQuestionnaireAnswers}
            positionList={positionList}
            setPositionList={setPositionList}
            onSavePositionList={onSavePositionList}
            onSaveJobTitleMatches={onSaveJobTitleMatches}
            jobTitleMatches={jobTitleMatches}
            setJobTitleMatches={setJobTitleMatches}
            matchedGrades={matchedGrades}
            setMatchedGrades={setMatchedGrades}
            highlightOverwriteButton={highlightedButton === "overwriteGrades"}
          />
        </DrawerRollup>

        {/* ── Save Success Alert ── */}
        {showSaveSuccess && (
          <Alert severity="success">
            {t("organizationSettings.settingsSaved")}
          </Alert>
        )}

        {/* ── Single Save & Close Buttons ── */}
        <Stack direction="row" justifyContent="flex-end" spacing={1} pt={1}>
          <Button
            variant="outlined"
            onClick={onClose}
            startIcon={<X size={16} />}
          >
            {t("paygap.gradeEstimator.close", "Close")}
          </Button>
          <Button variant="contained" onClick={handleSaveAll}>
            {t("paygap.gradeEstimator.save", "Save")}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
